import { useState } from "react";
import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";
import { client, result, type Schema } from "./api";

// PDF page numbers include covers and do not identify the printed drawing sheet.
export function sourcePageLabel(document: string, sheet: string, drawingSheet?: string | null) {
  if (drawingSheet && /\.pdf$/iu.test(document)) return `Лист ${drawingSheet} · страница PDF ${sheet}`;
  if (/\.pdf$/iu.test(document)) return `Страница PDF ${sheet || "…"}`;
  return /^(лист(?:\s|$)|model$)/iu.test(sheet) ? sheet : `Лист ${sheet || "…"}`;
}

export function useSourceLabels(revisionId: string, ids: string[]) {
  const unique = [...new Set(ids)].sort();
  const batches: string[][] = [];
  for (let i = 0; i < unique.length; i += 100) batches.push(unique.slice(i, i + 100));
  const queries = useQueries({ queries: batches.map(sourceIds => ({
    queryKey: ["source-labels", revisionId, sourceIds], staleTime: 300_000,
    queryFn: () => result(client.GET("/api/v1/revisions/{revision_id}/source-labels", {
      params: { path: { revision_id: revisionId }, query: { source_ids: sourceIds } },
    })),
  })) });
  return { labels: queries.flatMap(q => q.data || []), error: queries.find(q => q.error)?.error };
}

// Viewing an attachment must never change the saved list of evidence.
export function SourceReferences({ revisionId, value, change, openSource, addresses = [] }: {
  revisionId: string; value: string[]; change?: (ids: string[]) => void;
  openSource: (id: string, key?: string) => void; addresses?: Schema['SourceAddress'][];
}) {
  const [selected, setSelected] = useState("");
  const [record, setRecord] = useState("");
  const [editing, setEditing] = useState(false);
  const { labels, error } = useSourceLabels(revisionId, value);
  const current = value.includes(selected) ? selected : addresses.find(a => value.includes(a.source_id))?.source_id || value[0];
  const records = [...new Set(addresses.filter(a => a.source_id === current).map(a => a.record_key))];
  const currentRecord = records.includes(record) ? record : records[0];
  return <div className="source-references">
    <div className="source-reference-view"><label>Источники · {value.length}
      <select aria-label="Посмотреть источник" disabled={!value.length} value={current || ""} onChange={e => setSelected(e.target.value)}>
        {!value.length && <option value="">Источник не указан</option>}
        {value.map((id, i) => { const label = labels.find(l => l.id === id); return <option key={id} value={id}>{label ? `${i + 1}. ${label.document} · ${sourcePageLabel(label.document, label.sheet, label.drawing_sheet)}` : `Источник ${i + 1}`}</option>; })}
      </select></label>
      <button type="button" disabled={!current} onClick={() => openSource(current, currentRecord)}>Открыть</button>
    </div>
    {records.length>1&&<label>Область источника<select aria-label="Область источника" value={currentRecord} onChange={e=>setRecord(e.target.value)}>{records.map((key,i)=><option key={key} value={key}>Область {i+1} из {records.length}</option>)}</select></label>}
    {error && <p role="alert">Не удалось загрузить названия источников</p>}
    {change && <><button type="button" className="quiet" aria-expanded={editing} onClick={() => setEditing(!editing)}>{editing ? "Свернуть источники" : "Изменить источники"}</button>
      {editing && <SourcePicker revisionId={revisionId} value={value} change={ids => { if (ids.join() !== value.join()) change(ids); }} />}</>}
  </div>;
}

export function useDocumentSources(documentId: string) {
  const pageSize = 50;
  return useInfiniteQuery({
    queryKey: ["document-source-pages", documentId], enabled: !!documentId,
    initialPageParam: 0,
    queryFn: ({ pageParam }) => result(client.GET("/api/v1/documents/{document_id}/sources", {
      params: { path: { document_id: documentId }, query: { offset: pageParam, limit: pageSize } },
    })),
    getNextPageParam: (last, pages) => last.length === pageSize ? pages.length * pageSize : undefined,
  });
}

export function SourcePicker({ revisionId, value, change }: { revisionId: string; value: string[]; change: (ids: string[]) => void }) {
  const [documentId, setDocumentId] = useState("");
  const documents = useQuery({ queryKey: ["documents", revisionId], queryFn: () => result(client.GET("/api/v1/revisions/{revision_id}/documents", { params: { path: { revision_id: revisionId } } })) });
  const pages = useDocumentSources(documentId);
  const selected = useSourceLabels(revisionId, value);
  return <fieldset className="source-picker"><legend>Источники работы</legend>
    {value.map(id => { const label = selected.labels.find(l => l.id === id); return <div className="selected-source" key={id}><span>{label?.document || "Источник"} · {sourcePageLabel(label?.document || "", label?.sheet || "", label?.drawing_sheet)}</span><button type="button" className="quiet" onClick={() => change(value.filter(key => key !== id))}>Убрать</button></div>; })}
    <label className="field"><span>Добавить источник из документа</span><select value={documentId} onChange={e => setDocumentId(e.target.value)}><option value="">Выберите документ</option>{documents.data?.filter(d => d.status === "ready").map(d => <option value={d.id} key={d.id}>{d.path}</option>)}</select></label>
    <div className="source-options">{pages.data?.pages.flat().map(source => <label key={source.id}><input type="checkbox" checked={value.includes(source.id)} onChange={e => change(e.target.checked ? [...value, source.id] : value.filter(id => id !== source.id))} /><span>{sourcePageLabel(documents.data?.find(d => d.id === documentId)?.path || "", source.sheet)} · {source.source_key}<small>{source.text.slice(0, 150)}</small></span></label>)}</div>
    {pages.hasNextPage && <button type="button" onClick={() => void pages.fetchNextPage()} disabled={pages.isFetchingNextPage}>Ещё фрагменты</button>}
    {(pages.error || documents.error || selected.error) && <p className="error">Не удалось загрузить источники</p>}
  </fieldset>;
}
